import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { mapCustomAttributesFromResponse } from '@/modules/common/mappers/custom-attribute.mapper';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { CustomAttributeResponse } from '@/modules/common/types/dto/response/custom-attribute-response';
import { EntityTypeDTO } from '@/modules/common/types/dto/response/entity-type-dto';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { ExtendDirection } from '@/modules/common/types/extend-direction';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import {
  mapAvailableArchivationDateRangeFromDTO,
  mapAvailableUnarchivationDateFromDTO,
} from '@/modules/projects/mappers/available-archivation-dates.mapper';
import { mapExtendDirectionToDTO } from '@/modules/projects/mappers/project-delete-change.mapper';
import { mapProjectIntegrationsToPatchRequest } from '@/modules/projects/mappers/project-integration.mapper';
import {
  PatchProjectChangeParams,
  ProjectFormData,
  ProjectIntegration,
} from '@/modules/projects/types';
import { AvailableArchivationDateRange } from '@/modules/projects/types/available-archivation-dates';
import { AvailableProjectArchivationDateResponse } from '@/modules/projects/types/dto/response/available-project-archivation-date-response';
import { AvailableProjectUnarchivationDateResponse } from '@/modules/projects/types/dto/response/available-project-unarchivation-date-response';
import { ProjectFullResponse } from '@/modules/projects/types/dto/response/project-full-response';
import { ProjectSummaryResponse } from '@/modules/projects/types/dto/response/project-summary-response';
import { UpdateProjectResultResponse } from '@/modules/projects/types/dto/response/update-project-result-response';
import {
  Project,
  ProjectIndustry,
  ProjectListItem,
  ProjectStatus,
} from '@/modules/projects/types/project';
import { axiosInstance } from '@/utils/axios';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import {
  mapProjectChangesFromResponse,
  mapProjectChangeTypeToDTO,
} from '../mappers/project-changes.mapper';
import {
  mapProjectBillingConflictsFromDTO,
  mapProjectInfoConflictsFromDTO,
} from '../mappers/project-conflicts.mapper';
import {
  mapProjectDetailsFromResponse,
  mapProjectFormDataToCreateRequest,
  mapProjectFormDataToPatchRequest,
  mapProjectSummaries,
} from '../mappers/project.mapper';
import {
  ProjectBillingListResponseDTO,
  ProjectInfoListResponseDTO,
} from '../types/dto/project-conflict.dto';
import { DeleteProjectChangeRequest } from '../types/dto/request/delete-project-change-request';
import { PatchProjectChangeRequest } from '../types/dto/request/patch-project-change-request';
import { GetProjectChangesHistoryRequest } from '../types/dto/request/project-changes-request';
import { ProjectChangeMetadataResponse } from '../types/dto/response/project-change-response';
import { ProjectChangeLogEntry, ProjectChangeType } from '../types/project-changes';
import { ConflictResolutionData } from '../types/project-conflicts';

interface GetProjectsParams {
  cursor?: number;
  filters?: Record<string, any>;
}

export const getProjects = async ({
  cursor,
  filters = {},
}: GetProjectsParams): Promise<PaginatedResponse<ProjectListItem>> => {
  const endpoint = '/api/projects/summaries/search';

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.PROJECTS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: PaginatedResponse<ProjectSummaryResponse>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      filter: filters,
      pagination: {
        pageSize: DEFAULT_PAGE_SIZE,
        cursor: cursor,
      },
    })
  );

  return new PaginatedResponse({
    items: mapProjectSummaries(apiData.items),
    pagination: apiData.pagination,
  });
};

/**
 * Result type for getProjectDetails that includes both the domain model
 * and raw DTOs needed for UI operations (like merging with unlinked integrations).
 */
export interface ProjectDetailsResult {
  project: Project;
  rawIntegrations: IntegrationInfoResponse[];
}

/**
 * Retrieves detailed project information by ID.
 * Returns full project data including info, billing, and integrations.
 * Also returns raw integration DTOs for merging operations without contaminating domain model.
 */
export const getProjectDetails = async (projectId: number): Promise<ProjectDetailsResult> => {
  const endpoint = '/api/projects/one/full';

  const projectApiResponse = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_NOT_FOUND,
    () =>
      axiosInstance.get<{
        data: ProjectFullResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: { id: projectId },
      })
  );

  // Extract raw integrations before mapping
  const rawIntegrations = projectApiResponse.integrations.integrations;

  // Map API response DTO to domain model
  const mappedProject = mapProjectDetailsFromResponse(projectApiResponse);

  return {
    project: mappedProject,
    rawIntegrations: rawIntegrations || [],
  };
};

/**
 * Fetches full project state at a specific revision date.
 * Used in EditChange mode to load historical project data.
 * Uses the same endpoint as getProjectDetails but with a revisionDate parameter.
 * Returns full Project data (info + billing) at that point in time.
 *
 * GET /api/projects/one/full?id={id}&revisionDate={date}
 */
export const getProjectDetailsByRevisionDate = async (
  projectId: number,
  revisionDate: Date
): Promise<Project> => {
  const endpoint = '/api/projects/one/full';

  const projectApiResponse = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_NOT_FOUND,
    () =>
      axiosInstance.get<{
        data: ProjectFullResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: {
          id: projectId,
          revisionDate: normalizeToStartOfDayUTC(revisionDate).toISOString(),
        },
      })
  );

  return mapProjectDetailsFromResponse(projectApiResponse);
};

/**
 * Creates a new project using the full API structure.
 * Accepts ProjectFormData and transforms it to the API format.
 * Returns the created project ID for subsequent operations (e.g., logo upload).
 */
export const createProject = async (data: ProjectFormData): Promise<number> => {
  const endpoint = '/api/projects/one';
  const apiRequest = mapProjectFormDataToCreateRequest(data);

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_CREATE_FAILED, () =>
    axiosInstance.post<{
      data: { projectId: number };
      error?: { status: number; message: string; code: string };
    }>(endpoint, apiRequest)
  );

  // Note: Backend returns { projectId: number }, not standard CreatedEntity format
  return response.projectId;
};

/**
 * Updates existing project using the bulk update API.
 * Accepts ProjectFormData and transforms it to the API format.
 */
export const updateProject = async (
  projectId: string | number,
  data: Partial<ProjectFormData>,
  effectiveStartDate?: Date,
  currentStatus?: ProjectStatus,
  effectiveEndDate?: Date | null
): Promise<PatchEntityResult> => {
  const endpoint = '/api/projects/one';
  const patchRequest = mapProjectFormDataToPatchRequest({
    data,
    projectId,
    effectiveStartDate,
    currentStatus,
    effectiveEndDate,
  });

  return await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: UpdateProjectResultResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Updates project integrations only.
 * Uses dedicated integrations endpoint that doesn't require effectiveStartDate.
 */
export const updateProjectIntegrations = async (
  projectId: string | number,
  integrations?: ProjectIntegration[]
): Promise<void> => {
  const endpoint = '/api/projects/integrations';
  const patchRequest = mapProjectIntegrationsToPatchRequest(projectId, integrations);

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Updates a specific project change.
 * Used in EditChange mode to update an existing change record.
 * Maps form data to API request format and sends PATCH request.
 */
export const patchProjectChange = async (params: PatchProjectChangeParams): Promise<void> => {
  const endpoint = '/api/project-changes';

  // Map form data to API request format
  const patchRequest = mapProjectFormDataToPatchRequest({
    data: params.data,
    projectId: params.projectId,
    effectiveStartDate: params.effectiveStartDate,
    effectiveEndDate: params.effectiveEndDate,
  }) as PatchProjectChangeRequest;

  // Add the changeId to the request
  patchRequest.changeId = params.changeId;

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, patchRequest)
  );
};

/**
 * Archives a project with optional archive date.
 * Archived projects are hidden from active views but data is preserved.
 */
export const archiveProject = async ({
  projectId,
  archiveDate,
}: {
  projectId: string;
  archiveDate?: Date;
}): Promise<void> => {
  const endpoint = '/api/projects/archive';

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_ARCHIVE_FAILED, () =>
    axiosInstance.post<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      projectId,
      archivedDate: archiveDate ? normalizeToStartOfDayUTC(archiveDate).toISOString() : undefined,
    })
  );
};

/**
 * Restores an archived project to active status with effective start date.
 * Makes the project visible and trackable again.
 */
export const unarchiveProject = async ({
  projectId,
  unarchiveDate,
}: {
  projectId: string;
  unarchiveDate: Date;
}): Promise<void> => {
  const endpoint = `/api/projects/unarchive`;

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_UNARCHIVE_FAILED, () =>
    axiosInstance.post<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      projectId,
      startDate: normalizeToStartOfDayUTC(unarchiveDate).toISOString(),
    })
  );
};

/**
 * Retrieves available industry categories for project classification.
 */
export const getIndustries = async (): Promise<ProjectIndustry[]> => {
  const endpoint = '/api/industries';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.INDUSTRIES_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: { industries: ProjectIndustry[] };
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        locale: 'en-US',
      },
    })
  );

  return response.industries;
};

/**
 * Retrieves custom field definitions for project metadata.
 * Supports organization-specific project data requirements.
 */
export const getCustomAttributes = async (): Promise<CustomAttribute[]> => {
  const endpoint = '/api/custom-attributes';

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.CUSTOM_ATTRIBUTES_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: CustomAttributeResponse[];
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: {
          entityType: EntityTypeDTO.PROJECT,
        },
      })
  );

  return mapCustomAttributesFromResponse(apiData);
};

/**
 * Permanently deletes multiple projects and all associated data.
 * Warning: This action cannot be undone. Consider archiving instead.
 */
export const deleteProjects = async (projectIds: number[]): Promise<void> => {
  const endpoint = '/api/projects/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      data: { ids: projectIds },
    })
  );
};

/**
 * Retrieves the available archivation date range for a project.
 * Returns the start and end dates when a project can be archived.
 * GET /api/projects/archive/available-date
 */
export const getAvailableArchivationDateRange = async (
  projectId: string
): Promise<AvailableArchivationDateRange> => {
  const endpoint = '/api/projects/archive/available-date';

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_AVAILABLE_ARCHIVE_DATE_FAILED,
    () =>
      axiosInstance.get<{
        data: AvailableProjectArchivationDateResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: {
          projectId: parseInt(projectId, 10),
        },
      })
  );

  return mapAvailableArchivationDateRangeFromDTO(apiData);
};

/**
 * Retrieves the available unarchivation date for a project.
 * Returns the earliest date when a project can be unarchived.
 * POST /api/projects/unarchive/available-date
 */
export const getAvailableUnarchivationDate = async (projectId: string): Promise<Date | null> => {
  const endpoint = '/api/projects/unarchive/available-date';

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_AVAILABLE_UNARCHIVE_DATE_FAILED,
    () =>
      axiosInstance.post<{
        data: AvailableProjectUnarchivationDateResponse;
        error?: { status: number; message: string; code: string };
      }>(
        endpoint,
        {},
        {
          params: {
            projectId: parseInt(projectId, 10),
          },
        }
      )
  );

  return mapAvailableUnarchivationDateFromDTO(apiData);
};

/**
 * Uploads project logo image file using presigned URL approach.
 * Steps:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to S3 using presigned URL
 * 3. Return the final accessible URL
 */
export const uploadProjectLogo = async ({
  projectId,
  file,
}: {
  projectId: string;
  file: File;
}): Promise<string> => {
  const endpoint = '/api/projects/update-image-url';

  // Step 1: Get presigned URL from backend
  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_LOGO_UPLOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: { presignedUrl: string };
        error?: { status: number; message: string; code: string };
      }>(endpoint, {
        params: {
          projectId: parseInt(projectId, 10),
          fileName: file.name,
          contentLength: file.size,
        },
      })
  );

  const { presignedUrl } = response;

  // Step 2: Upload file to S3 using presigned URL
  await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  // Step 3: Extract the base URL (without query parameters) as the accessible logo URL
  return presignedUrl.split('?')[0];
};

/**
 * Retrieves project change history with pagination and filtering.
 * Returns list of changes made to project info, billing, and integrations.
 */
export const getProjectChanges = async (
  request: GetProjectChangesHistoryRequest
): Promise<PaginatedResponse<ProjectChangeLogEntry>> => {
  const endpoint = '/api/project-changes/search';

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_CHANGES_LOAD_FAILED,
    () =>
      axiosInstance.post<{
        data: {
          items: ProjectChangeMetadataResponse[];
          pagination: { total: number; cursor: number | null };
        };
        error?: { status: number; message: string; code: string };
      }>(endpoint, request)
  );

  return new PaginatedResponse({
    items: mapProjectChangesFromResponse(apiData.items),
    pagination: apiData.pagination,
  });
};

/**
 * Checks for conflicts in project info changes for a specific date range
 * Returns list of existing snapshots that will be affected by the change
 *
 * @param projectId - ID of the project
 * @param effectiveStartDate - Start date of the new change (ISO format)
 * @param effectiveEndDate - Optional end date of the new change (ISO format)
 * @returns Conflict resolution data with affected snapshots
 */
export const getProjectInfoConflicts = async ({
  projectId,
  effectiveStartDate,
  effectiveEndDate,
}: {
  projectId: number;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
}): Promise<ConflictResolutionData> => {
  const endpoint = '/api/projects/info';

  const params: Record<string, any> = {
    projectId,
    effectiveStartDate,
  };

  if (effectiveEndDate) {
    params.effectiveEndDate = effectiveEndDate;
  }

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_INFO_CONFLICTS_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: ProjectInfoListResponseDTO;
        error?: { status: number; message: string; code: string };
      }>(endpoint, { params })
  );

  return mapProjectInfoConflictsFromDTO(response);
};

/**
 * Checks for conflicts in project billing changes for a specific date range
 * Returns list of existing snapshots that will be affected by the change
 *
 * @param projectId - ID of the project
 * @param effectiveStartDate - Start date of the new change (ISO format)
 * @param effectiveEndDate - Optional end date of the new change (ISO format)
 * @returns Conflict resolution data with affected snapshots
 */
export const getProjectBillingConflicts = async ({
  projectId,
  effectiveStartDate,
  effectiveEndDate,
}: {
  projectId: number;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
}): Promise<ConflictResolutionData> => {
  const endpoint = '/api/projects/billing';

  const params: Record<string, any> = {
    projectId,
    effectiveStartDate,
  };

  if (effectiveEndDate) {
    params.effectiveEndDate = effectiveEndDate;
  }

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PROJECT_BILLING_CONFLICTS_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: ProjectBillingListResponseDTO;
        error?: { status: number; message: string; code: string };
      }>(endpoint, { params })
  );

  return mapProjectBillingConflictsFromDTO(response);
};

/**
 * Deletes a project change and extends/shifts adjacent change as specified
 *
 * @param projectId - ID of the project
 * @param section - Section type (INFO or BILLING)
 * @param effectiveStartDate - Start date of change to delete (ISO format)
 * @param extendDirection - Whether to extend previous or shift next change
 */
export const deleteProjectChange = async ({
  projectId,
  section,
  effectiveStartDate,
  extendDirection,
}: {
  projectId: number;
  section: ProjectChangeType;
  effectiveStartDate: string;
  extendDirection: ExtendDirection;
}): Promise<void> => {
  const endpoint = '/api/project-changes';

  const requestBody: DeleteProjectChangeRequest = {
    projectId,
    section: mapProjectChangeTypeToDTO(section),
    effectiveStartDate,
    extendDirection: mapExtendDirectionToDTO(extendDirection),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.PROJECT_CHANGE_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      data: requestBody,
    })
  );
};
